/**
 * Security Headers Service
 * Implements security best practices through meta tags and header validation
 */

/**
 * Security headers configuration
 */
interface SecurityHeadersConfig {
  /** Enforce HTTPS */
  enforceHTTPS?: boolean;

  /** Enable HSTS (HTTP Strict Transport Security) */
  enableHSTS?: boolean;

  /** HSTS max age in seconds */
  hstsMaxAge?: number;

  /** Include subdomains in HSTS */
  hstsIncludeSubdomains?: boolean;

  /** X-Frame-Options */
  xFrameOptions?: 'DENY' | 'SAMEORIGIN' | string;

  /** X-Content-Type-Options */
  xContentTypeOptions?: boolean;

  /** Referrer-Policy */
  referrerPolicy?: string;

  /** Permissions-Policy */
  permissionsPolicy?: Record<string, string[]>;
}

/**
 * Default security headers configuration
 */
const DEFAULT_CONFIG: SecurityHeadersConfig = {
  enforceHTTPS: true,
  enableHSTS: true,
  hstsMaxAge: 31536000, // 1 year
  hstsIncludeSubdomains: true,
  xFrameOptions: 'DENY',
  xContentTypeOptions: true,
  referrerPolicy: 'strict-origin-when-cross-origin',
  permissionsPolicy: {
    camera: [],
    microphone: [],
    geolocation: [],
    payment: [],
    usb: [],
  },
};

/**
 * Security Headers Service
 */
export class SecurityHeadersService {
  private config: SecurityHeadersConfig;

  constructor(config: Partial<SecurityHeadersConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Check if current connection is secure (HTTPS)
   */
  public isHTTPS(): boolean {
    return window.location.protocol === 'https:';
  }

  /**
   * Enforce HTTPS by redirecting if needed
   */
  public enforceHTTPS(): void {
    if (!this.config.enforceHTTPS) {
      return;
    }

    // In production, redirect HTTP to HTTPS
    if (!this.isHTTPS() && import.meta.env.PROD) {
      const httpsURL = window.location.href.replace('http:', 'https:');
      console.warn('[Security] Redirecting to HTTPS:', httpsURL);
      window.location.replace(httpsURL);
    }

    // In development, warn about HTTP
    if (!this.isHTTPS() && import.meta.env.DEV) {
      console.warn('[Security] Development mode: HTTP connection detected. Use HTTPS in production.');
    }
  }

  /**
   * Inject security meta tags
   * NOTE: X-Frame-Options and frame-ancestors cannot be set via meta tags
   * They must be HTTP headers (set by Cloudflare Worker)
   */
  public injectSecurityHeaders(): void {
    // X-Frame-Options - CANNOT be set via meta tag, must be HTTP header
    // Set by Cloudflare Worker instead
    // if (this.config.xFrameOptions) {
    //   this.createMetaTag('X-Frame-Options', this.config.xFrameOptions);
    // }

    // X-Content-Type-Options (prevents MIME sniffing)
    if (this.config.xContentTypeOptions) {
      this.createMetaTag('X-Content-Type-Options', 'nosniff');
    }

    // Referrer-Policy
    if (this.config.referrerPolicy) {
      this.createMetaTag('Referrer-Policy', this.config.referrerPolicy);
    }

    // Permissions-Policy
    if (this.config.permissionsPolicy) {
      const policy = this.buildPermissionsPolicy(this.config.permissionsPolicy);
      this.createMetaTag('Permissions-Policy', policy);
    }

    console.log('[SecurityHeaders] Security headers injected');
  }

  /**
   * Build Permissions-Policy header value
   */
  private buildPermissionsPolicy(policy: Record<string, string[]>): string {
    const directives = Object.entries(policy).map(([feature, origins]) => {
      if (origins.length === 0) {
        return `${feature}=()`;
      }
      return `${feature}=(${origins.join(' ')})`;
    });

    return directives.join(', ');
  }

  /**
   * Create meta tag for HTTP header equivalent
   */
  private createMetaTag(name: string, content: string): void {
    const existing = document.querySelector(`meta[http-equiv="${name}"]`);
    if (existing) {
      existing.setAttribute('content', content);
      return;
    }

    const meta = document.createElement('meta');
    meta.httpEquiv = name;
    meta.content = content;
    document.head.appendChild(meta);
  }

  /**
   * Validate origin against whitelist
   */
  public isOriginAllowed(origin: string, allowedOrigins: string[]): boolean {
    // Allow same origin
    if (origin === window.location.origin) {
      return true;
    }

    // Check wildcard
    if (allowedOrigins.includes('*')) {
      return true; // WARNING: Only for development
    }

    // Check exact match
    if (allowedOrigins.includes(origin)) {
      return true;
    }

    // Check pattern match (*.example.com)
    return allowedOrigins.some((allowed) => {
      if (allowed.startsWith('*.')) {
        const domain = allowed.substring(2);
        return origin.endsWith(domain);
      }
      return false;
    });
  }

  /**
   * Check if request has valid HTTPS certificate
   */
  public async validateCertificate(url: string): Promise<boolean> {
    try {
      const urlObj = new URL(url);

      // Only validate HTTPS URLs
      if (urlObj.protocol !== 'https:') {
        return false;
      }

      // In browser, we can't directly validate cert
      // But we can check if fetch succeeds
      await fetch(urlObj.toString(), {
        method: 'HEAD',
        mode: 'no-cors',
      });

      return true;
    } catch (error) {
      console.error('[SecurityHeaders] Certificate validation failed:', error);
      return false;
    }
  }

  /**
   * Get security report
   */
  public getSecurityReport(): {
    https: boolean;
    headers: Record<string, string>;
    warnings: string[];
  } {
    const warnings: string[] = [];

    if (!this.isHTTPS() && import.meta.env.PROD) {
      warnings.push('Connection is not secure (HTTP)');
    }

    if (!this.config.xContentTypeOptions) {
      warnings.push('X-Content-Type-Options not enabled');
    }

    if (!this.config.xFrameOptions) {
      warnings.push('X-Frame-Options not configured');
    }

    const headers: Record<string, string> = {};

    if (this.config.xFrameOptions) {
      headers['X-Frame-Options'] = this.config.xFrameOptions;
    }

    if (this.config.xContentTypeOptions) {
      headers['X-Content-Type-Options'] = 'nosniff';
    }

    if (this.config.referrerPolicy) {
      headers['Referrer-Policy'] = this.config.referrerPolicy;
    }

    return {
      https: this.isHTTPS(),
      headers,
      warnings,
    };
  }
}

/**
 * Singleton security headers service instance
 */
let securityHeadersInstance: SecurityHeadersService | null = null;

/**
 * Initialize security headers service
 */
export function initSecurityHeaders(
  config?: Partial<SecurityHeadersConfig>
): SecurityHeadersService {
  if (securityHeadersInstance) {
    return securityHeadersInstance;
  }

  securityHeadersInstance = new SecurityHeadersService(config);
  securityHeadersInstance.enforceHTTPS();
  securityHeadersInstance.injectSecurityHeaders();

  return securityHeadersInstance;
}

/**
 * Get security headers service instance
 */
export function getSecurityHeaders(): SecurityHeadersService {
  if (!securityHeadersInstance) {
    throw new Error('SecurityHeadersService not initialized. Call initSecurityHeaders first.');
  }
  return securityHeadersInstance;
}
